import { ReactElement, useEffect, useState } from "react";
import { ROLES } from "../../auth/Roles";
import Dot from "../dot/Dot";
import DotAction from "../dot/DotAction";
import { useAppSelector } from "../../store/hooks";
import useAuthentication from "../../auth/useAuthentication";
import { useTimeout } from "../../auth/useTimeout";
import useIsAlertIgnored from "../../useIsAlertIgnored";
import { alertsAdapter } from "../underlines/writingAlertsSlice";
import { editorAdapter } from "../../editorsSlice";
import BrandingIcon from "../icons/BrandingIcon";
import { DotSize, DotState } from "../icons/DotIcon";
import useHoverDelay from "../underlines/hooks/useHoverDelay";
import DotPocket from "./DotPocket";
import BubbleIcon from "../icons/BubbleIcon";
import { PLAN_PAGE_ENDPOINT } from "../../../../../routes/endpointConstants";

type ColorString = string;
export const DOT_OVERLAY_COLOR: ColorString = "rgba(209, 97, 149, 0.3)";

interface Props {
  editorId: number;
  editorElement: Element;
  visible?: boolean;

  /// whether hover states show the dot pocket
  interactive?: boolean;
  debugColor?: ColorString;

  // If the application code has thrown an exception
  hasError?: boolean;
}

const MARGIN = 8;
const LOADING_TIMEOUT_MS = 20_000; // 20 seconds

const VERSION = "beta";

function DotController({
  editorId,
  editorElement,
  visible = true,
  interactive = true,
  debugColor,
  hasError = false,
}: Props): ReactElement {
  const { isHovering, forceNotHovering, markIsHovering, markIsNotHovering } =
    useHoverDelay(100, 300);
  const shouldHover = isHovering && interactive;
  const editor = useAppSelector((state) =>
    editorAdapter.getSelectors().selectById(state.editors, editorId)
  );
  const isAlertIgnored = useIsAlertIgnored(editorElement);
  const numberOfErrors = useAppSelector(
    (state) =>
      alertsAdapter
        .getSelectors()
        .selectAll(state.writingAlerts)
        .filter((a) => !isAlertIgnored(a))
        .filter((a) => a.editorId === editorId).length
  );
  const [hasLoadedBefore, setHasLoaded] = useState(false);

  const { user } = useAuthentication();
  let gated = false;
  if (
    !user?.roles.includes(ROLES.PREMIUM_M) &&
    !user?.roles.includes(ROLES.PREMIUM_Y)
  ) {
    let preText = editor!.content;
    preText = preText.replace(/[!#$%&()*,./:;=^_`{}~]\n/g, " ");
    const words = preText.split(" ");
    if (words.length >= 250) {
      gated = true;
    }
  }
  const isTimedOut = useTimeout(editor?.isLoading || false, LOADING_TIMEOUT_MS);

  useEffect(() => {
    if (editor?.isLoading) {
      setHasLoaded(true);
    }
  }, [editor?.isLoading, hasLoadedBefore, setHasLoaded]);

  if (!visible || !editor) {
    return <></>;
  }
  let state: DotState = "default";
  if (numberOfErrors > 0) {
    state = "error";
  } else if (hasLoadedBefore) {
    state = "correct";
  }

  const text = numberOfErrors ? `${numberOfErrors}` : undefined;
  const { isLoading } = editor;

  let content = <></>;
  const size: DotSize = "normal";
  let callout = <></>;
  if (gated) {
    callout = (
      <DotAction
        onClick={() => {
          const url = `${PLAN_PAGE_ENDPOINT}`;
          window.open(url, "_blank");
        }}
        label="Upgrade to Premium"
      >
        <BubbleIcon className="w-4 h-4" />
      </DotAction>
    );
  }
  if (isTimedOut || hasError) {
    content = (
      <div
        className="pointer-events-auto"
        title="Loading your feedback is taking too long. Try refreshing the page."
      >
        <Dot state="critical" loading={false} size={size} gated={gated}>
          ⚠️
        </Dot>
      </div>
    );
  } else {
    content = !shouldHover ? (
      <Dot state={state} loading={isLoading} size={size} gated={gated}>
        {isLoading ? "" : text}
      </Dot>
    ) : (
      <DotPocket>
        {/* Settings Action (Not yet implemented) */}
        <div className="px-1.5">
          <BrandingIcon />
        </div>
        {callout}
        {/* <DotAction label="Test">Test</DotAction> */}
        <Dot
          state={state}
          loading={isLoading}
          size="normal"
          onClick={() => forceNotHovering()}
          gated={gated}
        >
          {isLoading ? "" : text}
        </Dot>
      </DotPocket>
    );
  }

  return (
    <div
      className={`absolute right-0 bottom-0 ${
        interactive ? "pointer-events-auto" : "pointer-events-none"
      }`}
      style={{
        marginBottom: MARGIN,
        marginRight: MARGIN,
        background: debugColor || "transparent",
      }}
      onMouseOver={() => {
        markIsHovering();
      }}
      onMouseLeave={() => {
        markIsNotHovering();
      }}
      onFocus={() => {
        markIsHovering();
      }}
      onBlur={() => {
        markIsNotHovering();
      }}
    >
      {content}
    </div>
  );
}

export default DotController;
